import type { GlyphColor, GlyphKind, SphereOwnGlyph } from 'types/glyph';

/** Vertex property names a sphere's own glyph lives in (the `center_glyph` aspect). */
export const SPHERE_GLYPH_PROPERTY_NAMES = ['glyph', 'glyph_kind', 'glyph_color'] as const;

/** Used only when neither the sphere nor its type carries a glyph. */
export const DEFAULT_SPHERE_GLYPH = {
  glyph: 'lucide:circle',
  glyphColor: { kind: 'hex', value: '#64748B' },
} as const;

/** An Iconify `set:name`, or exactly one non-space character (a letter glyph). */
export const GLYPH_VALUE_PATTERN = /^(?:[a-z0-9-]+:[a-z0-9-]+|[^\s:])$/;

const TOKEN_PATTERN = /^[a-z][a-z0-9-]{0,47}$/;
const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/;

/**
 * Strict shape check for a glyph colour. The value reaches a CSS variable name,
 * a `--glyph-hue` property and an SVG fill, so anything outside these three
 * closed forms is rejected rather than escaped. A key other than `kind` and
 * `value` is rejected too: the colour is served at discover level on every list
 * read, so it must never carry an arbitrary payload alongside a valid colour.
 */
export function isValidGlyphColor(v: unknown): v is GlyphColor {
  if (typeof v !== 'object' || v === null) return false;
  if (!Object.keys(v).every((key) => key === 'kind' || key === 'value')) return false;
  const { kind, value } = v as { kind?: unknown; value?: unknown };
  switch (kind) {
    case 'token':
      return typeof value === 'string' && TOKEN_PATTERN.test(value);
    case 'hue':
      return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 359;
    case 'hex':
      return typeof value === 'string' && HEX_PATTERN.test(value);
    default:
      return false;
  }
}

function unwrap(value: unknown): unknown {
  return Array.isArray(value) ? value[0] : value;
}

/** The kind a glyph value's shape implies: an Iconify `set:name` or a single letter. */
export function glyphKindOf(glyph: string): GlyphKind {
  return glyph.includes(':') ? 'iconify' : 'letter';
}

/** A stored own-glyph property that was present but failed validation. */
export type MalformedGlyphProperty = 'glyph' | 'glyph_color';

/** An absent stored value: missing, null, or the `''` a Gremlin `coalesce(..., constant(''))` projects. */
function isAbsent(value: unknown): boolean {
  return value === undefined || value === null || value === '';
}

/**
 * Parses a sphere's own glyph values from a Gremlin valueMap, a
 * properties().group() result or a flattened bag: single-element arrays are
 * unwrapped, a string `glyph_color` is JSON-parsed, and invalid values are dropped.
 * The kind is always derived from the glyph's shape, never trusted from storage,
 * and the colour is a fresh `{ kind, value }`, never the stored object.
 * `onMalformed` is called once per present-but-invalid `glyph` or `glyph_color`,
 * so a server caller can log it; an absent value never triggers it.
 */
export function readOwnSphereGlyph(
  props: Record<string, unknown> | null | undefined,
  onMalformed?: (property: MalformedGlyphProperty) => void
): SphereOwnGlyph | null {
  if (!props) return null;
  const own: SphereOwnGlyph = {};

  const glyph = unwrap(props['glyph']);
  if (typeof glyph === 'string' && GLYPH_VALUE_PATTERN.test(glyph)) {
    own.glyph = glyph;
    own.glyphKind = glyphKindOf(glyph);
  } else if (!isAbsent(glyph)) {
    onMalformed?.('glyph');
  }

  const storedColor = unwrap(props['glyph_color']);
  let color = storedColor;
  if (typeof color === 'string' && color !== '') {
    try {
      color = JSON.parse(color);
    } catch {
      color = undefined;
    }
  }
  if (isValidGlyphColor(color)) {
    own.glyphColor = { kind: color.kind, value: color.value } as GlyphColor;
  } else if (!isAbsent(storedColor)) {
    onMalformed?.('glyph_color');
  }

  return Object.keys(own).length > 0 ? own : null;
}

export interface ResolvedSphereGlyph {
  glyph: string;
  glyphKind: GlyphKind;
  glyphColor: GlyphColor;
  glyphSource: 'sphere' | 'type' | 'default';
  glyphColorSource: 'sphere' | 'type' | 'default';
}

/**
 * Resolves the effective glyph and colour independently: each comes from the
 * sphere's own value, else the type's, else the default. The default glyph
 * applies only when the type has no glyph, and the default colour only when
 * neither the sphere nor the type has a valid one.
 */
export function resolveSphereGlyph(
  own: SphereOwnGlyph | null,
  type: { glyph?: string | null; glyphColor?: GlyphColor | null } | null
): ResolvedSphereGlyph {
  let glyph: string;
  let glyphSource: ResolvedSphereGlyph['glyphSource'];
  if (own?.glyph) {
    glyph = own.glyph;
    glyphSource = 'sphere';
  } else if (type?.glyph) {
    glyph = type.glyph;
    glyphSource = 'type';
  } else {
    glyph = DEFAULT_SPHERE_GLYPH.glyph;
    glyphSource = 'default';
  }

  let glyphColor: GlyphColor;
  let glyphColorSource: ResolvedSphereGlyph['glyphColorSource'];
  if (isValidGlyphColor(own?.glyphColor)) {
    glyphColor = own.glyphColor;
    glyphColorSource = 'sphere';
  } else if (isValidGlyphColor(type?.glyphColor)) {
    glyphColor = type.glyphColor;
    glyphColorSource = 'type';
  } else {
    glyphColor = { ...DEFAULT_SPHERE_GLYPH.glyphColor };
    glyphColorSource = 'default';
  }

  return { glyph, glyphKind: glyphKindOf(glyph), glyphColor, glyphSource, glyphColorSource };
}

/** ID-3: true when the sphere carries any own visual value (an own glyph or an own colour). */
export function hasOwnGlyph(own: SphereOwnGlyph | null): boolean {
  return !!own && (!!own.glyph || isValidGlyphColor(own.glyphColor));
}
